import React, { useContext, useMemo, useState } from 'react';

import { Spin, message } from 'antd';

import { ListContext } from '@contexts/ListContext';

import { loadModelContentById, useModelContents, useAddModelContent, useEditModelContent, useRemoveModelContent, useToggleModelContentPublished } from '@hooks/API/ModelContents/UseModelContents';
import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';

import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import { NwAddButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';

import ModelContentEditorDrawer from '@components/ModelContents/ModelContentEditorDrawer';
import ModelContentItem from '@components/ModelContents/ModelContentItem';

const MODEL_CONTENT_TAG_TYPE = 'ModelContent';

const getRecordTagIds = (record) => {
    const tagIds = Array.isArray(record.TagIDs) ? record.TagIDs : record.TagIds;
    if (!Array.isArray(tagIds)) return [];
    return tagIds.map(Number).filter(Number.isFinite);
};

const getRecordInlineTags = (record) => {
    if (!Array.isArray(record.Tags)) return [];
    return record.Tags.filter((tag) => tag && tag.Name);
};

const ModelContentsDrawer = ({ canManageContent = true, modelID, onClose }) => {
    const { cachedList } = useContext(ListContext);
    const { data: modelContents, isLoading } = useModelContents(modelID);
    const { mutate: addContent } = useAddModelContent();
    const { mutate: editContent } = useEditModelContent();
    const { mutate: removeContent } = useRemoveModelContent();
    const { mutate: togglePublished } = useToggleModelContentPublished();
    const adjustTagUsageCount = useAdjustTagUsageCount();

    const [editorOpen, setEditorOpen] = useState(false);
    const [editorContent, setEditorContent] = useState(null);
    const [editorInitialView, setEditorInitialView] = useState('preview');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingContent, setIsLoadingContent] = useState(false);

    const tagById = useMemo(() => {
        const modelContentTags = cachedList.publictags.items
            .concat(cachedList.privatetags.items)
            .filter((tag) => tag.TagTypeName === MODEL_CONTENT_TAG_TYPE);

        return new Map(modelContentTags.map((tag) => [Number(tag.ID), tag]));
    }, [cachedList.privatetags.items, cachedList.publictags.items]);

    const getRecordTags = (record) => {
        const tagsFromIds = getRecordTagIds(record)
            .map((tagId) => tagById.get(tagId))
            .filter(Boolean);

        return tagsFromIds.length > 0 ? tagsFromIds : getRecordInlineTags(record);
    };

    const handleCreate = () => {
        if (!canManageContent) return;
        setEditorContent(null);
        setEditorInitialView('edit');
        setEditorOpen(true);
    };

    const handleOpenContent = async (record, initialView = 'preview') => {
        setIsLoadingContent(true);
        try {
            const fullContent = await loadModelContentById(record.ID);
            setEditorContent(fullContent);
            setEditorInitialView(initialView);
            setEditorOpen(true);
        } catch (error) {
            message.error('Unable to load content');
        } finally {
            setIsLoadingContent(false);
        }
    };

    const handleCloseEditor = () => {
        setEditorOpen(false);
        setEditorContent(null);
        setEditorInitialView('preview');
    };

    const handleSave = async (contentToSave) => {
        setIsSaving(true);
        try {
            const savedContent = await new Promise((resolve, reject) => {
                const mutationVars = { ...contentToSave };

                if (contentToSave.ID) {
                    editContent(mutationVars, {
                        onSuccess: resolve,
                        onError: reject,
                    });
                } else {
                    // Remove ID from create payload (it may be a local temp ID)
                    const { ID, ...createVars } = mutationVars;
                    addContent(createVars, {
                        onSuccess: resolve,
                        onError: reject,
                    });
                }
            });
            // Keep the editor's modelContent in sync with the persisted record (with its new ID),
            // otherwise the next save on a freshly created content would POST again instead of PUT.
            setEditorContent(savedContent);
            message.success('Content saved');
        } catch (error) {
            message.error('Unable to save content');
            throw error;
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = (record) => {
        if (!canManageContent) return;
        const removedTagIds = getRecordTagIds(record);
        removeContent(
            { ID: record.ID, ModelID: modelID },
            {
                onSuccess: () => {
                    // il contenuto eliminato non usa piu' i suoi tag
                    removedTagIds.forEach((tagId) => adjustTagUsageCount(tagId, -1));
                    message.success('Content deleted');
                },
                onError: () => message.error('Unable to delete content'),
            }
        );
    };

    const handleTogglePublished = (record) => {
        if (!canManageContent) return;
        togglePublished(
            { ID: record.ID, ModelID: modelID, Published: !record.Published },
            {
                onError: () => message.error('Unable to update published state'),
            }
        );
    };

    const contents = Array.isArray(modelContents) ? modelContents : [];

    return (
        <NwDrawer whiteBg={false} title="Model Contents" width="50rem" onClose={onClose}>
            {canManageContent && (
                <div className="drawer-toolbar-container">
                    <NwAddButton primary label="New content" onClick={handleCreate} />
                </div>
            )}
            <div className="drawer-content standard">
                {isLoading ? (
                    <Loading />
                ) : contents.length > 0 ? (
                    <Spin spinning={isLoadingContent}>
                        {contents.map((content) => (
                            <ModelContentItem
                                key={content.ID}
                                canManageContent={canManageContent}
                                content={content}
                                tags={getRecordTags(content)}
                                onDelete={() => handleDelete(content)}
                                onOpen={(initialView) => handleOpenContent(content, initialView)}
                                onTogglePublished={() => handleTogglePublished(content)}
                            />
                        ))}
                    </Spin>
                ) : (
                    <EmptyState message="No contents" style={{ marginTop: '5rem' }} />
                )}
            </div>

            {editorOpen && (
                <ModelContentEditorDrawer
                    canManageContent={canManageContent}
                    initialView={editorInitialView}
                    modelContent={editorContent}
                    modelID={modelID}
                    saving={isSaving}
                    zIndex={1080}
                    onClose={handleCloseEditor}
                    onSave={handleSave}
                />
            )}
        </NwDrawer>
    );
};

export default ModelContentsDrawer;
